import _ from 'lodash';
import moment from 'moment';
import onboardingApi from '@/api/OnbordingApi';
import phoneNumberUtils from '@/utils/phoneNumber';
import Segment from '../../Segment';
import Intercom from '../../Intercom';
import Sentry from '../../Sentry';
import DDLogs from '../../DDLogs';
import CustomerApi from '../../api/CustomersApi';
import CustomerActivityApi from '../../api/CustomerActivityApi';
import HansApi from '../../api/HansApi';
import SubscriptionStateApi from '../../api/SubscriptionStateApi';
import InvalidCredentialsStateApi from '../../api/InvalidCredentialsStateApi';

const DAYS_IN_TRIAL_VALUE_FOR_CUSTOMERS_NOT_IN_TRIAL = -1;

function _getDaysSince(subscriptionState) {
  return moment().diff(moment(subscriptionState.since), 'days');
}

const state = {
  authenticated: false,
  inviteToken: '',
  customerId: null,
  customerDetails: {},
  members: [],
  activeMember: null,
  activeMemberPhoneNumber: null,
};

const getters = {
  firstName: (state, getters) => getters.primaryMember.firstNamePiiValue,
  activeMemberFirstName: state => state.activeMember.firstNamePiiValue,
  activeMemberPhoneNumber: state => phoneNumberUtils.formatPhoneNumber(state.activeMemberPhoneNumber),
  numberOfMembers: state => state.members.length,
  householdName: state => {
    const customerPrimaryMember = _.find(state.members, m => m.primary);
    const secondMemberCreated = _.chain(state.members)
      .filter(m => !m.primary)
      .sortBy(m => m.created)
      .first()
      .value();
    return _.compact([
      customerPrimaryMember && customerPrimaryMember.firstNamePiiValue,
      secondMemberCreated && secondMemberCreated.firstNamePiiValue,
    ]).join(' ו');
  },
  primaryMember: state => {
    const primaryMember = state.customerDetails.primaryMember || {};
    return {
      firstNamePiiValue: primaryMember.firstNamePiiValue,
      lastNamePiiValue: primaryMember.lastNamePiiValue,
      phoneNumberPiiValue: phoneNumberUtils.formatPhoneNumber(primaryMember.phoneNumberPiiValue),
    };
  },
};

const actions = {
  async initRiseupAccount({ commit }, phoneNumber) {
    const members = await CustomerApi.getMembers();
    const activeMember = members.find(m => phoneNumberUtils.comparePhoneNumbers(m.phoneNumberPiiValue, phoneNumber));
    commit('setActiveMember', activeMember);
    commit('setAuthenticated', true);
  },
  async init({ commit, getters }, { incognito, name }) {
    try {
      const customerDetails = await CustomerApi.getCustomerDetails();
      const { onboardingState } = await onboardingApi.fetchOnboardingState();
      const onboardingStatus = await CustomerApi.getOnboradingStatus();
      const members = await CustomerApi.getMembers();
      const inviteToken = await HansApi.generateInviteToken();
      const activeMember = await CustomerApi.getActiveMember();
      commit('setActiveMember', activeMember);
      commit('setActiveMemberPhoneNumber', activeMember.phoneNumberPiiValue);
      const subscriptionState = await SubscriptionStateApi.fetchSubscriptionState();
      const invalidCredentialsState = await InvalidCredentialsStateApi.fetchInvalidCredentialsState();
      commit('setCustomerId', customerDetails.customerId);
      DDLogs.addGlobalContext('customerId', customerDetails.customerId);
      commit('setAuthenticated', true);
      commit('setCustomerDetails', customerDetails);
      commit('setMembers', members);
      commit('setInviteToken', inviteToken.token);
      Segment.setIncognito(incognito);
      Segment.identifyCustomer(customerDetails.customerId);
      if (!incognito) {
        const isOnboarding = onboardingStatus !== 'completed';
        const daysInTrial = subscriptionState.state === 'trial' ? _getDaysSince(subscriptionState) : DAYS_IN_TRIAL_VALUE_FOR_CUSTOMERS_NOT_IN_TRIAL;
        const daysInInvalidCredentials = invalidCredentialsState ? Math.abs(moment(invalidCredentialsState.since).diff(moment(), 'days')) : 0;
        const primaryMember = _.find(members, { primary: true });
        // we have very few customer with more than 2 members
        const secondMember = _.find(members, { primary: false });
        const email = primaryMember ? primaryMember.emailPiiValue : null;
        const { householdName } = getters;
        await Intercom.boot(
          customerDetails.customerId, householdName,
          {
            userHash: customerDetails.intercomUserHash,
            numberOfMembers: getters.numberOfMembers,
            isOnboarding,
            activatedAt: onboardingState && onboardingState.activatedAt,
            daysInTrial,
            daysInInvalidCredentials,
            email,
            phone: primaryMember.phoneNumberPiiValue,
            firstName1: primaryMember.firstNamePiiValue,
            phone2: secondMember && secondMember.phoneNumberPiiValue,
            firstName2: secondMember && secondMember.firstNamePiiValue,
            mfaEnabled: _.every(state.members, 'oidc'),
          },
        );
        Sentry.initCustomerId(customerDetails.customerId);
        await CustomerActivityApi.setCustomerActivity({
          activityTime: moment(),
          details: {
            activityType: 'visitPage',
            pageName: name,
          },
        });
      }
    } catch (e) {
      // Added this in order to avoid sentry issues ith 403 errors
      console.log('Could not authenticate', e);
    }
  },
  setAuthenticated({ commit }, value) {
    commit('setAuthenticated', value);
  },
};

const mutations = {
  setAuthenticated(state, authenticateRespond) {
    state.authenticated = authenticateRespond;
  },
  setActiveMember(state, member) {
    state.activeMember = member;
  },
  setActiveMemberPhoneNumber(state, phoneNumber) {
    state.activeMemberPhoneNumber = phoneNumber;
  },
  setCustomerId(state, customerId) {
    state.customerId = customerId;
  },
  setCustomerDetails(state, customerDetails) {
    state.customerDetails = customerDetails;
  },
  setMembers(state, members) {
    state.members = members;
  },
  setInviteToken(state, inviteToken) {
    state.inviteToken = inviteToken;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
